/**
 * The chest stage: Blockari's `ChestStage` (apps/client/src/screens/
 * ChestScreen.tsx), ported out of React line for line. The arrival ("you've
 * earned a chest": the veil, the beam, the pedestal slam, TAP TO OPEN /
 * LATER, the flight into the chest pill) and the reveal it hands over to
 * (the climb that plays itself after one tap, the open, the cards, COLLECT)
 * are Blockari's clocks and Blockari's scene; the words are placed per frame
 * from them exactly as Blockari places them.
 *
 * 21 Wild's changes are the theme only: the palette (./palette.ts), the type
 * (the display face), and the reward cards (./reward-card.ts), which are
 * Blockari's `.chest-card` in 21 Wild's stock: the same slot, flight, glint
 * and landing sparks, with the table's gold, felt and violet, a suit pip in the
 * corner, and 21 Wild's own reward art. The cards, the effects and the timing
 * are Blockari's.
 *
 * The stage mounts over everything (Blockari's "floating" case, the one it
 * uses over its results screen): 21 Wild's screens each carry their own top
 * bar, so there is no single shell to sit under.
 */

import { RARITIES, type ChestSource, type Rarity } from '@/game/chests';
import type { ChestBeat } from '@/audio/ceremony-kit';
import { playChestBeat } from '@/ceremony/sound';
import { chestOdds, type ChestView } from '@/net/api';
import type { Quality } from '@/pixi/quality';
import { el } from '@/ui/hud';
import { coin } from '@/ui/screens';

import {
  ArrivalClock,
  arrivalTitle,
  chestLabel,
  MAX_SHOWN,
  shown,
  labelSize,
  titleSize,
  type ArrivalEvent,
  type ArrivalFrame,
  type ArrivalView,
} from './arrival-timeline';
import {
  arrivalOpened,
  beginStow,
  currentPillCount,
  markArrivalSeen,
  markArrivalUp,
  setArrivalCovered,
  stowChest,
  type ArrivalChest,
} from './arrival-state';
import { STILL_FILL, type Handoff } from './chest-pose';
import { ChestScene, rarityTitleColor } from './chest-scene';
import {
  COLLECT_S,
  ChestTimeline,
  clamp01,
  easeOutBack,
  easeOutCubic,
  type ChestEvent,
} from './chest-timeline';
import { burst, chestIcon } from './glyphs';
import { collectLabel, openSignup, signupStore, viewerStore } from './signup-state';
import { RARITY_MATERIALS } from './palette';
import { copyOf, mountReward, paintReward, preloadArt, resetReward, type RewardCard } from './reward-card';

const KICKER: Record<ChestSource, string> = {
  rank_up: 'RANK UP',
  first_win: 'FIRST WIN',
  wins_10: '10 WINS',
  wins_50: '50 WINS',
  wins_100: '100 WINS',
  win_streak_3: 'WIN STREAK',
  login_streak_7: '7-DAY STREAK',
  weekly_top: 'WEEKLY TOP 3',
  welcome: 'WELCOME GIFT',
};

/**
 * The kicker over a chest: its source's, but a weekly chest names its place and board ("2ND PLACE \u00b7 WEEKLY $CHAIN")
 * and a streak chest its run ("5 WINS IN A ROW"): what it was for, the shared `chestLabel`. Other sources keep theirs.
 */
const kickerOf = (c: { source: ChestSource; sourceRef?: string | undefined }): string =>
  c.source === 'win_streak_3' || c.source === 'weekly_top' ? chestLabel(c) : KICKER[c.source];

const RARITY_LABEL: Record<Rarity, string> = {
  common: 'COMMON',
  rare: 'RARE',
  epic: 'EPIC',
  legendary: 'LEGENDARY',
};

/** The pill's icon size (ui.css: .w21-chest-pill svg). */
const PILL_ICON = 20;
/** The layer's fade after the last COLLECT of an arrival. */
const CLOSE_S = 0.32;
/** After a hand-over, the reveal's title slams in once the arrival's has gone. */
const HEAD_AFTER_HANDOFF_S = 0.12;
/** The sparks a card throws as it lands where it sits (Blockari's `scene.sparkleAt(sx, sy, 14)`). */
const LAND_SPARKS = 14;

/** One chest the stage shows: its summary and, once known, its stored roll. */
export type StageChest = ArrivalChest;

export interface ChestStageOptions {
  /** The chests in order: the reveal opens [0] first, then each next one after COLLECT. */
  chests: readonly StageChest[];
  /** Play the arrival first (TAP TO OPEN / LATER); false: the reveal's own land. */
  arrival: boolean;
  dev: boolean;
  quality: Quality;
  /** Capture: the clock is parked and stepped by the harness (`hook`). */
  debug?: boolean;
  /** The last COLLECT has finished (and, for an arrival, the layer has faded). */
  onFinish?: () => void;
  /** LATER's last flight has landed and the layer has faded. */
  onClose?: () => void;
  /** A chest's reveal starts: apply it server-side (idempotent). */
  onOpen?: (id: string) => void;
  /** Every beat, as it is heard (the capture mixes the clip's sound from these). */
  onBeat?: (beat: ChestBeat, step: number, at: number) => void;
  /** $CHAIN has landed in the balance (the account should refresh). */
  onBalance?: () => void;
}

/** The capture harness's handle, as Blockari's `window.__chest`. */
export interface ChestHook {
  readonly timeline: ChestTimeline | null;
  readonly arrival: ArrivalClock | null;
  readonly t: number;
  step(ms: number): void;
  tap(): boolean;
  collect(): boolean;
  open(): boolean;
  later(): boolean;
  readonly done: boolean;
  readonly current: string | null;
}

/** The top bar's chest pill on screen, or null. */
function visiblePill(): HTMLElement | null {
  for (const p of document.querySelectorAll<HTMLElement>('.w21-topbar .w21-chest-pill svg')) {
    if (p.getBoundingClientRect().width > 0) return p;
  }
  return null;
}

/** The results' game chrome (the quit and gear buttons) is on screen: its corners are taken. */
function chromeOnScreen(): boolean {
  for (const c of document.querySelectorAll<HTMLElement>('.w21-game-chrome:not(.w21-game-chrome--docked)')) {
    if (c.getBoundingClientRect().width > 0) return true;
  }
  return false;
}

/** The balance pill's coin on screen, or null. */
function visibleBalanceCoin(): HTMLElement | null {
  for (const c of document.querySelectorAll<HTMLElement>('.w21-balance .w21-coin')) {
    if (c.getBoundingClientRect().width > 0) return c;
  }
  return null;
}

/** The avatar on screen (the rest of a chest's rewards fly to it when there is no pill). */
function visibleAvatar(): HTMLElement | null {
  for (const a of document.querySelectorAll<HTMLElement>('.w21-avatar-link')) {
    if (a.getBoundingClientRect().width > 0) return a;
  }
  return null;
}

const reducedMotion = (): boolean =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Blockari's coin flight (shell/money.ts `flyCoin`): an arc from one element to another. */
const COIN_FLY_MS = 480;
const COIN_ARC_PX = 60;
function flyFrom(a: DOMRect, glyph: HTMLElement, to: Element | null): Promise<void> {
  if (!to || reducedMotion()) return Promise.resolve();
  const b = to.getBoundingClientRect();
  if (a.width === 0 || b.width === 0) return Promise.resolve();
  glyph.classList.add('w21-coin-fly');
  glyph.setAttribute('aria-hidden', 'true');
  Object.assign(glyph.style, {
    left: `${a.left}px`,
    top: `${a.top}px`,
    width: `${a.width}px`,
    height: `${a.height}px`,
  });
  document.body.appendChild(glyph);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  const scale = b.width / a.width;
  const mid = (1 + scale) / 2;
  const anim = glyph.animate(
    [
      { transform: 'translate(0, 0) scale(1)' },
      { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - COIN_ARC_PX}px) scale(${mid})` },
      { transform: `translate(${dx}px, ${dy}px) scale(${scale})` },
    ],
    { duration: COIN_FLY_MS, easing: 'cubic-bezier(0.65, 0, 0.35, 1)', fill: 'forwards' },
  );
  return new Promise((resolve) => {
    const done = (): void => {
      glyph.remove();
      resolve();
    };
    anim.addEventListener('finish', done, { once: true });
    anim.addEventListener('cancel', done, { once: true });
  });
}


export class ChestStage {
  readonly root: HTMLElement;
  readonly #over: HTMLElement | null;
  readonly #opts: ChestStageOptions;
  #hook: ChestHook | null = null;
  #teardown: (() => void) | null = null;
  /** The chests, with their rolls as they come in (`setRoll`). */
  #queue: StageChest[];

  constructor(opts: ChestStageOptions) {
    this.#opts = opts;
    this.#queue = [...opts.chests];
    const arrival = opts.arrival;
    const chests = this.#queue;
    const first = chests[0];
    const count = chests.length;
    const shownChests = chests.slice(0, MAX_SHOWN);
    const mixed = shownChests.some((c) => c.source !== shownChests[0]?.source);

    // --- the DOM (Blockari's JSX, element for element) -----------------------
    const root = el('div', `w21-chest-screen floating${opts.dev ? ' dev' : ''}${arrival ? ' arrival arriving' : ''}`);
    root.dataset.quality = opts.quality;
    root.dataset.rarity = first?.startRarity ?? 'common';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Chest');
    this.root = root;
    const canvasHost = el('div', 'w21-chest-canvas');
    canvasHost.setAttribute('aria-hidden', 'true');
    root.append(canvasHost);

    let aTitle: HTMLElement | null = null;
    let aCount: HTMLElement | null = null;
    if (arrival) {
      const head = el('div', 'w21-arrival-head');
      head.setAttribute('aria-live', 'polite');
      const title = arrivalTitle(shownChests);
      aTitle = el('div', `w21-arrival-title${titleSize(title)}`, title);
      aTitle.style.opacity = '0.001';
      head.append(aTitle);
      if (count > 1) {
        aCount = el('div', 'w21-arrival-count', `×${count}`);
        aCount.style.opacity = '0.001';
        head.append(aCount);
      }
      root.append(head);
    }
    const tags: HTMLElement[] = [];
    if (mixed) {
      for (const c of shownChests) {
        const tag = el('div', 'w21-arrival-tag', chestLabel(c));
        tag.style.opacity = '0.001';
        tags.push(tag);
        root.append(tag);
      }
    }
    const headEl = el('div', 'w21-chest-head');
    const kickerEl = el('div', 'w21-chest-kicker');
    kickerEl.style.opacity = '0.001';
    const titleEl = el('div', 'w21-chest-title');
    titleEl.style.opacity = '0.001';
    const titleText = el('span');
    titleEl.append(titleText);
    const ladder = el('div', 'w21-chest-ladder');
    ladder.setAttribute('aria-hidden', 'true');
    const pips = RARITIES.map((r) => {
      const pip = el('i', `r-${r}`);
      pip.style.opacity = '0.001';
      ladder.append(pip);
      return pip;
    });
    headEl.append(kickerEl, titleEl, ladder);
    root.append(headEl);
    const tapEl = el('div', 'w21-chest-tap', 'TAP TO OPEN');
    tapEl.setAttribute('aria-hidden', 'true');
    root.append(tapEl);

    let actions: HTMLElement | null = null;
    let openBtn: HTMLButtonElement | null = null;
    let laterBtn: HTMLButtonElement | null = null;
    if (arrival) {
      actions = el('div', 'w21-arrival-actions');
      actions.style.opacity = '0.001';
      actions.style.pointerEvents = 'none';
      openBtn = el('button', 'w21-btn w21-warm w21-lg w21-arrival-open', 'TAP TO OPEN');
      openBtn.type = 'button';
      laterBtn = el('button', 'w21-arrival-later', 'LATER');
      laterBtn.type = 'button';
      actions.append(openBtn, laterBtn);
      root.append(actions);
    }
    const oddsBtn = el('button', 'w21-chest-odds-btn');
    oddsBtn.type = 'button';
    oddsBtn.setAttribute('aria-label', 'Chest odds');
    oddsBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">'
      + '<circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01" stroke-linecap="round"/></svg> ODDS';
    oddsBtn.style.opacity = '0.001';
    root.append(oddsBtn);
    const cardsRow = el('div', 'w21-chest-cards');
    root.append(cardsRow);
    const collectBtn = el('button', 'w21-btn w21-warm w21-lg w21-chest-collect', 'COLLECT');
    collectBtn.type = 'button';
    collectBtn.style.opacity = '0.001';
    collectBtn.style.pointerEvents = 'none';
    root.append(collectBtn);

    // LATER's flyers and the dock pill: over everything, on the body.
    const flyers: HTMLElement[] = [];
    let dockEl: HTMLElement | null = null;
    let dockCount: HTMLElement | null = null;
    if (arrival) {
      const over = el('div', 'w21-arrival-over');
      over.setAttribute('aria-hidden', 'true');
      for (const c of shownChests) {
        const fl = el('div', `w21-arrival-flyer r-${c.startRarity}`);
        fl.style.opacity = '0';
        const streak = el('i', 'streak');
        const head = el('span', 'fallback head');
        head.append(chestIcon());
        fl.append(streak, head);
        flyers.push(fl);
        over.append(fl);
      }
      // The pill is under the stage (21 Wild's bars live in the screens), so the
      // dock always catches the flights, in the pill's own corner.
      dockEl = el('div', `w21-arrival-dock w21-chest-pill r-${first?.startRarity ?? 'common'}`);
      dockEl.style.opacity = '0';
      // Over the results the gear holds that corner (the game chrome, top-right): the dock lands
      // under it, on the same right edge, instead of on top of the button.
      if (chromeOnScreen()) dockEl.classList.add('below-chrome');
      dockCount = el('b', 'pill-n', '0');
      dockEl.append(chestIcon(), dockCount);
      over.append(dockEl);
      document.body.append(over);
      this.#over = over;
    } else {
      this.#over = null;
    }
    document.body.append(root);

    // --- the clocks (Blockari's effect, verbatim in behaviour) ---------------
    const debug = opts.debug === true;
    const reduced = reducedMotion();
    const plan = { count: this.#queue.length, reduced };
    const clock = arrival ? new ArrivalClock(plan) : null;
    const rarities = this.#queue.slice(0, MAX_SHOWN).map((c) => c.startRarity);
    const scene = new ChestScene(opts.quality, arrival ? shown(plan) : 0);
    scene.reduced = reduced;
    let tl: ChestTimeline | null = null;
    let handoff: Handoff | null = null;
    let index = 0;
    let raf = 0;
    let last = 0;
    let alive = true;
    let manual = debug;
    let shownRarity: Rarity | null = null;
    let shownKicker = '';
    let finished = false;
    let closeAt: number | null = null;
    let closed = false;
    let stowed = 0;
    let now = clock?.t ?? 0;

    // The reward cards: DOM (reward-card.ts), built per chest as its roll is known. Their art is fetched and
    // decoded before the first reveal starts (`artReady`), so no card waits on an image mid-flight.
    let rewards: RewardCard[] = [];
    let slots: HTMLElement[] = [];
    const landedAt: number[] = [];
    const artReady = preloadArt(this.#queue.flatMap((c) => (c.roll?.contents ?? []).map((it) => copyOf(it).art)));
    // The slots' places in the stage's own coordinates, measured once per build and per resize (not per frame).
    let geo: { slots: { x: number; y: number; h: number }[] } | null = null;

    // A guest sees the whole reveal but cannot claim: the button asks to sign up, and what they
    // open is held server-side until they do. `declined`: they said Later once on this stage, so
    // the rest of its chests are simply kept for later instead of asking again.
    const isGuest = (): boolean => viewerStore.getState().guest;
    let declined = false;

    const beat = (kind: ChestBeat, step = 0): void => {
      playChestBeat(kind, step);
      opts.onBeat?.(kind, step, now);
    };

    // window.innerWidth flushes layout: read on a resize, never in the frame (it ran after the frame's own style writes).
    let vw = window.innerWidth;
    let aTitleW = -1;
    let titleW = 0;
    let titleWFor: string | null = null;
    let cachedView: ArrivalView | null = null;
    const view = (): ArrivalView => {
      if (cachedView && !(clock?.dec.laterAt !== null && clock?.dec.laterAt !== undefined)) return cachedView;
      cachedView = measureView();
      return cachedView;
    };
    const measureView = (): ArrivalView => {
      const r = root.getBoundingClientRect();
      const pill = visiblePill();
      const dock = dockEl?.querySelector<SVGElement>('svg') ?? null;
      const target = pill && dockEl === null ? pill : dock;
      let p = { x: r.width - 44, y: 34 };
      if (target) {
        const b = target.getBoundingClientRect();
        if (b.width > 0) p = { x: b.left + b.width / 2 - r.left, y: b.top + b.height / 2 - r.top };
      }
      return { width: r.width, height: r.height, pill: p, pillIcon: PILL_ICON };
    };

    /** The cards' row for chest i: one slot per reward, a card in each (it flies into its slot). */
    let builtFor = '';
    const buildSlots = (i: number): void => {
      const roll = this.#queue[i]?.roll;
      // Already built (pre-warmed before the tap): keep the cards, whose layers are already rastered.
      if (this.#queue[i]?.id === builtFor && rewards.length === (roll?.contents.length ?? 0)) {
        for (const rc of rewards) resetReward(rc);
        landedAt.length = 0;
        geo = null;
        return;
      }
      builtFor = this.#queue[i]?.id ?? '';
      const items = roll?.contents ?? [];
      cardsRow.replaceChildren();
      cardsRow.dataset.n = String(items.length);
      slots = items.map(() => {
        const slot = el('div', 'w21-chest-slot');
        cardsRow.append(slot);
        return slot;
      });
      rewards = items.map((it, k) => mountReward(slots[k]!, it));
      landedAt.length = 0;
      geo = null;
    };

    const startReveal = (i: number, fromArrival: boolean): boolean => {
      const chest = this.#queue[i];
      const roll = chest?.roll;
      if (!chest || !roll) return false;
      index = i;
      tl = new ChestTimeline(roll.startRarity, roll.steps, roll.contents.length, { at: now, landed: fromArrival, reduced });
      if (fromArrival) {
        const from = scene.lastPose();
        handoff = from ? { at: now, from } : null;
        tl.tap();
        scene.flood(now);
      } else handoff = null;
      if (!fromArrival) {
        shownRarity = null;
        shownKicker = '';
      }
      finished = false;
      if (arrival) arrivalOpened(chest.id);
      opts.onOpen?.(chest.id);
      buildSlots(i);
      root.classList.remove('done', 'arriving');
      return true;
    };

    const apply = (dt: number): void => {
      const revealEvents = tl ? tl.drain() : [];
      const arrivalEvents = clock ? clock.drain() : [];
      if (clock && clock.dec.openAt !== null && !tl) startReveal(0, true);
      const showArrival = clock !== null && !(tl && index > 0);
      const v = view();
      const frame = showArrival && clock ? clock.frame(v) : null;
      if (tl) paint(tl, false);
      // The picture is the one part that can fail on a phone (the GPU takes the context back under
      // memory pressure, and a draw then throws). The title, buttons and clock are DOM and must go on
      // without it: an uncaught throw here ended the frame loop and left TAP TO OPEN dead.
      try {
        scene.render(tl, dt, revealEvents, frame ? { frame, rarities, events: arrivalEvents } : null, handoff);
      } catch (error) {
        if (!sceneFailed) console.warn('[w21] chest scene failed to draw; carrying on without it', error);
        sceneFailed = true;
      }
      for (const e of arrivalEvents) onArrivalEvent(e);
      for (const e of revealEvents) onEvent(e);
      if (frame) paintArrival(frame);
      if (tl) paint(tl, true);
      paintClose();
    };

    const onArrivalEvent = (e: ArrivalEvent): void => {
      switch (e.type) {
        case 'beam':
          beat('arrive-beam');
          break;
        case 'land':
          beat('arrive-land');
          break;
        case 'rattle':
          beat('arrive-rattle');
          break;
        case 'stow': {
          beat('arrive-stow');
          const n = this.#queue.length;
          const upto = e.index === shown(plan) - 1 ? n : e.index + 1;
          for (; stowed < upto; stowed++) {
            const c = this.#queue[stowed];
            if (c) stowChest(c.id);
          }
          if (dockCount) {
            dockCount.textContent = String(currentPillCount());
            // The count pops and the pill bursts as each one lands (Blockari's PillBurst).
            dockCount.classList.remove('pop');
            void dockCount.offsetWidth;
            dockCount.classList.add('pop');
            burst(dockEl);
          }
          break;
        }
        case 'done':
          if (!closed) {
            closed = true;
            opts.onClose?.();
          }
          break;
      }
    };

    const onEvent = (e: ChestEvent): void => {
      switch (e.type) {
        case 'land':
          // (21 Wild's land cue carries Blockari's LAND_FALL_S delay in its own table.)
          beat('land');
          break;
        case 'upgrade':
          beat('upgrade', e.index);
          break;
        case 'open':
          beat('open');
          break;
        case 'card':
          beat('card', e.index);
          break;
        case 'collect':
          beat('collect');
          void flyHome();
          break;
        default:
          break;
      }
    };

    const paintArrival = (f: ArrivalFrame): void => {
      if (aTitle) {
        const s = 1 + 0.9 * (1 - easeOutBack(f.title, 2.2));
        // offsetWidth ignores the transform, so it only changes with the text or a resize: measured once (a read per
        // frame was a forced layout after the frame's own style writes).
        if (aTitleW < 0) aTitleW = aTitle.offsetWidth;
        const room = Math.max(1, (vw - 32) / Math.max(1, aTitleW));
        const out = f.titleOut;
        aTitle.style.opacity = Math.max(0.001, clamp01(f.title * 3) * (1 - out) * f.layer).toFixed(3);
        aTitle.style.transform = `translateY(${(-10 * out).toFixed(2)}px) scale(${Math.min(room, s * (1 - 0.18 * out)).toFixed(4)})`;
      }
      if (aCount) {
        const pop = 1 + 0.7 * (1 - easeOutBack(f.count, 2.4));
        aCount.style.opacity = Math.max(0.001, clamp01(f.count * 3) * (1 - f.countOut) * f.layer).toFixed(3);
        aCount.style.transform = `scale(${pop.toFixed(4)})`;
      }
      if (actions) {
        const b = easeOutCubic(f.buttons) * (1 - f.buttonsOut);
        actions.style.opacity = Math.max(0.001, b * f.layer).toFixed(3);
        actions.style.transform = `translateY(${((1 - easeOutBack(f.buttons)) * 22 + f.buttonsOut * 8).toFixed(2)}px)`;
        actions.style.pointerEvents = f.live ? 'auto' : 'none';
        actions.style.setProperty('--breathe', f.live ? (0.5 + 0.5 * Math.sin(f.t * 3.4)).toFixed(3) : '0');
      }
      // A tag is at most one pedestal's pitch wide: a long label ("5 WINS IN A ROW") wraps under its chest
      // rather than running into its neighbour's or off the edge of a phone.
      const xs = f.chests.map((c) => c.slot.x).sort((p, q) => p - q);
      const pitch = xs.reduce((m, x, i) => (i > 0 ? Math.min(m, x - (xs[i - 1] ?? x)) : m), Infinity);
      const tagMax = Number.isFinite(pitch) ? `${Math.floor(pitch * 0.94)}px` : '';
      f.chests.forEach((c, i) => {
        const tag = tags[i];
        if (!tag) return;
        if (tag.style.maxWidth !== tagMax) tag.style.maxWidth = tagMax;
        const a = c.landed * (1 - f.titleOut) * f.layer * c.alpha;
        tag.style.opacity = Math.max(0.001, c.visible ? a : 0).toFixed(3);
        tag.style.transform = `translate(${c.slot.x.toFixed(1)}px, ${(c.slot.y + c.slot.size * 0.62).toFixed(1)}px) translateX(-50%)`;
      });
      if (f.veil >= 0.9) markArrivalUp();
      setArrivalCovered((f.veil >= 0.99 || f.flood >= 1) && f.layer >= 1);
      if (root.classList.contains('arrival-live') !== f.live) root.classList.toggle('arrival-live', f.live);
      f.chests.forEach((c, i) => {
        const fl = flyers[i];
        if (!fl) return;
        if (!c.flight) {
          fl.style.opacity = '0';
          return;
        }
        const f0 = c.flight;
        const head = fl.querySelector<HTMLElement>('.head');
        const streak = fl.querySelector<HTMLElement>('.streak');
        fl.style.opacity = '1';
        if (head) {
          const k = f0.size / STILL_FILL / 256;
          const st = f0.stretch;
          head.style.opacity = '1';
          head.style.transform = `translate(${(f0.x - 128).toFixed(2)}px, ${(f0.y - 128).toFixed(2)}px) scale(${(k * (1 - (st - 1) * 0.42)).toFixed(4)}, ${(k * st).toFixed(4)})`;
        }
        if (streak) {
          const dx = f0.x - f0.tail.x;
          const dy = f0.y - f0.tail.y;
          const len = Math.hypot(dx, dy) + f0.size * 0.5;
          const th = Math.max(8, (f0.size + f0.tail.size) * 0.42);
          const ang = Math.atan2(dy, dx);
          streak.style.opacity = f0.glow.toFixed(3);
          streak.style.width = `${len.toFixed(1)}px`;
          streak.style.height = `${th.toFixed(1)}px`;
          streak.style.transform = `translate(${f0.tail.x.toFixed(2)}px, ${(f0.tail.y - th / 2).toFixed(2)}px) rotate(${ang.toFixed(4)}rad)`;
        }
      });
      if (dockEl) {
        dockEl.style.opacity = f.dock.toFixed(3);
        dockEl.style.transform = `translateY(${((1 - f.dock) * -12).toFixed(2)}px) scale(${(0.9 + 0.1 * f.dock).toFixed(3)})`;
      }
    };

    /**
     * The reveal's DOM, from its timeline. `after`: the second pass, once the
     * scene has drawn (the words); the first pass places the cards, which the
     * scene draws, so they are in this frame and not the next.
     */
    const paint = (tl: ChestTimeline, after: boolean): void => {
      const t = tl.t;
      if (!after) {
        paintCards(tl);
        return;
      }
      const rarity = tl.rarity;
      const mat = RARITY_MATERIALS[rarity];
      const headAt = tl.handedOver ? tl.landedAt + HEAD_AFTER_HANDOFF_S : tl.landedAt;
      const changeAt = tl.popAt[tl.popped - 1] ?? headAt;
      const ts = clamp01((t - changeAt) / 0.3);
      if (shownRarity !== rarity) {
        shownRarity = rarity;
        titleText.textContent = `${RARITY_LABEL[rarity]} CHEST`;
        root.dataset.rarity = rarity;
      }
      if (titleWFor !== titleText.textContent) {
        titleWFor = titleText.textContent;
        titleW = titleEl.offsetWidth;
      }
      const room = Math.max(1, (vw - 32) / Math.max(1, titleW));
      const s = Math.min(room * 1.0, 1 + 0.9 * (1 - easeOutBack(ts, 2.2)));
      titleEl.style.transform = `scale(${s.toFixed(4)})`;
      titleEl.style.opacity = String(Math.max(0.001, clamp01(ts * 3)));
      titleEl.style.color = rarityTitleColor(rarity);
      titleEl.style.setProperty('--glow', `#${mat.glow.toString(16).padStart(6, '0')}`);
      const at = RARITIES.indexOf(rarity);
      // The rarity ladder stays through the reveal (it is how the player reads what the chest was), a little smaller
      // and lifted once the lid flies so the open lid does not sit on it.
      const ladderOpen = tl.openAt === null ? 0 : clamp01((t - tl.openAt) / 0.25);
      const ladderIn = Math.max(0.001, clamp01((t - headAt) / 0.25) * (1 - 0.12 * ladderOpen));
      ladder.style.transform = `translateY(${(-7 * ladderOpen).toFixed(2)}px) scale(${(1 - 0.12 * ladderOpen).toFixed(3)})`;
      pips.forEach((pip, i) => {
        pip.style.opacity = String(ladderIn);
        pip.classList.toggle('lit', i <= at);
        const pop = i === at ? 1 + 0.6 * (1 - easeOutBack(ts, 2.2)) : 1;
        pip.style.transform = `rotate(45deg) scale(${pop.toFixed(3)})`;
      });
      const head = this.#queue[index];
      const kicker =
        tl.openAt !== null ? 'YOU GOT' : tl.popped > 0 ? 'UPGRADE!' : kickerOf(head ?? { source: 'rank_up' });
      if (kicker !== shownKicker) {
        shownKicker = kicker;
        kickerEl.textContent = kicker;
        kickerEl.dataset.size = tl.openAt !== null || tl.popped > 0 ? '' : labelSize(kicker);
        kickerEl.dataset.kind = tl.openAt !== null ? 'got' : tl.popped > 0 ? 'upgrade' : 'source';
      }
      const kAt = tl.openAt ?? tl.popAt[tl.popped - 1] ?? (tl.handedOver ? headAt : headAt - 0.1);
      const ku = clamp01((t - kAt) / 0.22);
      kickerEl.style.opacity = String(Math.max(0.001, ku));
      const kpop = tl.popped > 0 && tl.openAt === null ? 1 + 0.5 * (1 - easeOutBack(ku, 2)) : 1;
      kickerEl.style.transform = `translateY(${((1 - easeOutCubic(ku)) * 8).toFixed(2)}px) scale(${kpop.toFixed(3)})`;
      const on = !tl.climbing && tl.openAt === null && (tl.phase === 'idle' || tl.tappable);
      tapEl.style.opacity = Math.max(0.001, on ? 0.72 + 0.28 * Math.sin(t * 4.2) : 0).toFixed(3);
      oddsBtn.style.opacity = clamp01((t - tl.landedAt) / 0.3).toFixed(3);
      const ready = tl.readyAt;
      const cu = ready === null ? 0 : clamp01((t - ready) / 0.28);
      const gone = tl.collectAt !== null ? clamp01((t - tl.collectAt) / 0.2) : 0;
      collectBtn.style.opacity = Math.max(0.001, easeOutCubic(cu) * (1 - gone)).toFixed(3);
      collectBtn.style.transform = `translateY(${((1 - easeOutBack(cu)) * 18).toFixed(2)}px)`;
      collectBtn.style.pointerEvents = tl.ready ? 'auto' : 'none';
      const label = collectLabel({ guest: isGuest(), declined });
      if (collectBtn.textContent !== label) collectBtn.textContent = label;
      if (!finished && tl.collectAt !== null && t - tl.collectAt >= COLLECT_S) {
        finished = true;
        finish();
      }
    };

    /** Where the slots are, in the stage's own coordinates (measured when asked, after a build or a resize). */
    const measureGeo = (): NonNullable<typeof geo> => {
      const host = root.getBoundingClientRect();
      return {
        slots: slots.map((slot) => {
          const r = slot.getBoundingClientRect();
          return { x: r.left - host.left + r.width / 2, y: r.top - host.top + r.height / 2, h: r.height };
        }),
      };
    };

    /**
     * Cards: out of the chest's mouth on an arc into their slots, one after another, each landing with a glint and a
     * spray of sparks (Blockari's loop, ChestScreen.tsx). All transforms and opacities, from the stage clock.
     */
    const paintCards = (tl: ChestTimeline): void => {
      const t = tl.t;
      geo ??= measureGeo();
      const mouth = scene.mouth();
      for (let i = 0; i < rewards.length; i++) {
        const rc = rewards[i]!;
        const slot = geo.slots[i];
        if (!slot) continue;
        const u = tl.cardProgress(i);
        const start = tl.cardStart(i);
        paintReward(rc, {
          u,
          started: start !== null && t >= start,
          from: { x: mouth.x - slot.x, y: mouth.y - slot.y },
          height: slot.h,
          collect: tl.collectAt === null ? 0 : clamp01((t - tl.collectAt - i * 0.05) / 0.32),
          reduced,
        });
        if (u >= 1 && landedAt[i] === undefined) {
          landedAt[i] = t;
          if (!reduced) scene.sparkleAt(slot.x, slot.y, LAND_SPARKS);
        }
      }
    };

    const paintClose = (): void => {
      if (closeAt === null) return;
      if (arrival) setArrivalCovered(false);
      const k = clamp01((now - closeAt) / CLOSE_S);
      root.style.opacity = Math.max(0.001, 1 - k).toFixed(3);
      if (k >= 1 && !closed) {
        closed = true;
        opts.onFinish?.();
      }
    };

    /** COLLECT: the $CHAIN card's coin flies to the balance pill; the rest to the chest pill. */
    const flyHome = async (): Promise<void> => {
      const items = this.#queue[index]?.roll?.contents ?? [];
      const flights: Promise<void>[] = [];
      items.forEach((it, i) => {
        const slot = slots[i];
        if (!slot) return;
        const r = slot.getBoundingClientRect();
        const size = r.width * 0.5;
        const from = new DOMRect(
          r.left + r.width / 2 - size / 2,
          r.top + r.height * 0.3 - size / 2,
          size,
          size,
        );
        if (isGuest()) {
          // Held for an account: nothing reached the balance. The cards go where the chest is kept.
          const glyph = el('span', 'w21-chest-fly-glyph');
          glyph.append(chestIcon());
          flights.push(flyFrom(from, glyph, visiblePill() ?? visibleAvatar()));
        } else if (it.type === 'chain') {
          flights.push(
            flyFrom(from, coin(), visibleBalanceCoin()).then(() => {
              if (!opts.dev) opts.onBalance?.();
            }),
          );
        } else {
          const glyph = el('span', 'w21-chest-fly-glyph');
          glyph.append(chestIcon());
          flights.push(flyFrom(from, glyph, visiblePill() ?? visibleAvatar()));
        }
      });
      await Promise.all(flights);
    };

    const finish = (): void => {
      if (arrival && index + 1 < this.#queue.length) {
        if (startReveal(index + 1, false)) return;
      }
      root.classList.add('done');
      if (!opts.dev && !isGuest()) opts.onBalance?.();
      if (arrival) {
        closeAt = now;
        return;
      }
      if (opts.dev || debug) return;
      opts.onFinish?.();
    };

    const advance = (dt: number): void => {
      now += dt;
      clock?.step(dt);
      tl?.step(dt);
    };

    let sceneFailed = false;
    let warmFrames = 0;
    let steady = 0;
    let warmStart = 0;
    let warm = true;
    const loop = (ts: number): void => {
      if (!alive) return;
      // A modal sheet (the sign-up sheet) covers the stage: nothing behind it needs drawing, and a
      // clock that keeps running would play the reveal out unseen. Hold the frame and the clock.
      if (signupStore.getState().open) {
        last = ts;
        raf = requestAnimationFrame(loop);
        return;
      }
      if (!alive) return;
      if (warm) {
        if (!warmStart) warmStart = ts;
        const gap = last ? ts - last : 99;
        steady = gap < 20 ? steady + 1 : 0;
        warmFrames++;
        if ((warmFrames > 4 && steady >= 8) || ts - warmStart > 1400) warm = false;
      }
      const dt = last && !warm ? Math.min(0.05, (ts - last) / 1000) : 0;
      last = ts;
      // Re-armed whatever the frame does: a throw anywhere in it must cost a frame, not the stage.
      try {
        advance(dt);
        apply(dt);
      } finally {
        raf = requestAnimationFrame(loop);
      }
    };

    const tapOpen = (): boolean => {
      if (!clock || !clock.open()) return false;
      startReveal(0, true);
      return true;
    };
    const tapLater = (): boolean => {
      if (!clock || !clock.later()) return false;
      beginStow();
      if (dockCount) dockCount.textContent = String(currentPillCount());
      return true;
    };

    const queue = (): StageChest[] => this.#queue;
    this.#hook = {
      get timeline() {
        return tl;
      },
      arrival: clock,
      get t() {
        return now;
      },
      step: (ms) => {
        manual = true;
        cancelAnimationFrame(raf);
        advance(ms / 1000);
        apply(ms / 1000);
      },
      tap: () => (tl ? tl.tap() : tapOpen()),
      collect: () => tl?.collect() ?? false,
      open: tapOpen,
      later: tapLater,
      get done() {
        return finished || closed;
      },
      get current() {
        return tl ? (queue()[index]?.id ?? null) : null;
      },
    };

    const onResize = (): void => {
      cachedView = null;
      vw = window.innerWidth;
      aTitleW = -1;
      titleWFor = null;
      geo = null;
      scene.resize(canvasHost.clientWidth, canvasHost.clientHeight);
      if (manual) apply(0);
    };


    // A renderer that cannot start (no WebGL left, a lost context) must not leave the stage up with its
    // clock stopped: the rest of the chain starts the loop, and the stage runs without a picture.
    this.ready = scene
      .init(canvasHost, canvasHost.clientWidth, canvasHost.clientHeight)
      .catch((error: unknown) => {
        sceneFailed = true;
        console.warn('[w21] chest scene could not start; carrying on without it', error);
      })
      .then(async () => {
        if (!alive) {
          scene.destroy();
          return;
        }
        const head = this.#queue[0];
        if (arrival && head) {
          shownRarity = head.startRarity;
          titleText.textContent = `${RARITY_LABEL[head.startRarity]} CHEST`;
          shownKicker = kickerOf(head);
          kickerEl.textContent = shownKicker;
          kickerEl.dataset.size = labelSize(shownKicker);
          kickerEl.dataset.kind = 'source';
        }
        if (arrival) {
          for (const r of new Set(rarities)) {
            const url = scene.bakeStill(r);
            if (!url) continue;
            this.#queue.slice(0, MAX_SHOWN).forEach((c, i) => {
              if (c.startRarity !== r) return;
              const fl = flyers[i];
              const old = fl?.querySelector('.head');
              if (!fl || !old) return;
              const img = el('img', 'head');
              img.src = url;
              img.alt = '';
              old.replaceWith(img);
            });
          }
        }
        // The first reveal's cards are baked before the clock starts (a bake mid-reveal costs frames).
        await artReady;
        // Build the first chest's cards NOW, at a whisper of opacity: the compositor rasters their layers (the
        // material, the foil, the art) during the arrival or the idle, not on the frame each card first appears.
        if (alive && this.#queue[0]?.roll) {
          buildSlots(0);
          for (const rc of rewards) resetReward(rc);
        }
        if (!alive) return;
        if (!arrival) startReveal(0, false);
        try {
          apply(0);
        } finally {
          if (!manual) raf = requestAnimationFrame(loop);
        }
      });

    const onPointer = (e: PointerEvent): void => {
      if ((e.target as HTMLElement).closest('button, .w21-chest-odds-sheet')) return;
      if (tl) {
        tl.tap();
        return;
      }
      if (!clock) return;
      const r = root.getBoundingClientRect();
      const f = clock.frame(view());
      const hit = f.chests.some(
        (c) => c.visible && Math.hypot(e.clientX - r.left - c.pose.x, e.clientY - r.top - c.pose.y) < c.pose.size * 0.75,
      );
      if (hit) tapOpen();
    };
    root.addEventListener('pointerdown', onPointer);
    openBtn?.addEventListener('click', () => tapOpen());
    laterBtn?.addEventListener('click', () => tapLater());
    collectBtn.addEventListener('click', () => {
      if (!tl) return;
      // A guest's COLLECT is the sign-up moment: the sheet asks, and "Later" carries on with the
      // chest kept (it is held; nothing was applied).
      if (isGuest() && !declined && tl.ready && tl.collectAt === null) {
        const current = tl;
        openSignup('chest', () => {
          declined = true;
          current.collect();
        });
        return;
      }
      tl.collect();
    });
    oddsBtn.addEventListener('click', () => openOdds());
    window.addEventListener('resize', onResize);

    this.#setRoll = (id, roll) => {
      const c = this.#queue.find((x) => x.id === id);
      if (!c) return;
      c.roll = roll;
    };

    this.#teardown = () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      scene.destroy();
      if (arrival) setArrivalCovered(false);
    };

    if (!arrival) markArrivalSeen(this.#queue.map((c) => c.id));
  }

  /** Resolves once the scene is up and the first frame has been drawn. */
  readonly ready: Promise<void>;

  /** The capture harness's handle (`window.__chest` under `?debug=1`). */
  get hook(): ChestHook | null {
    return this.#hook;
  }

  #setRoll: (id: string, roll: ChestView) => void = () => {};

  /** A stored roll came in for one of the batch's chests (fetched as the arrival starts). */
  setRoll(id: string, roll: ChestView): void {
    this.#setRoll(id, roll);
  }

  destroy(): void {
    this.#teardown?.();
    this.#teardown = null;
    this.root.remove();
    this.#over?.remove();
  }
}

/* --------------------------------------------------------------------------
   The odds sheet (Blockari's OddsSheet): the published table, from the server.
   -------------------------------------------------------------------------- */

const RANKS_FROM = ['Deuce', 'Deuce', 'Seven', 'Ten', 'Jack', 'Queen'];

function rarityName(r: Rarity): HTMLElement {
  const word = `${RARITY_LABEL[r][0]}${RARITY_LABEL[r].slice(1).toLowerCase()}`;
  const span = el('span', 'w21-rarity-name');
  span.style.setProperty('--rc', rarityTitleColor(r));
  span.append(el('span', 'rn-full', word));
  const short = el('span', 'rn-short', word.slice(0, r === 'legendary' || r === 'common' ? 3 : 4));
  short.setAttribute('aria-hidden', 'true');
  span.append(short);
  return span;
}

export function openOdds(): void {
  const overlay = el('div', 'w21-chest-odds-overlay');
  const sheet = el('div', 'w21-dialog w21-chest-odds-sheet');
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-labelledby', 'w21-odds-title');
  const head = el('div', 'w21-panel-head');
  const h2 = el('h2', undefined, 'Chest odds');
  h2.id = 'w21-odds-title';
  head.append(h2, el('span', 'w21-chip w21-mint', 'Earned · never sold'));
  const body = el('div', 'w21-chest-odds-body');
  body.append(el('span', 'w21-spinner'));
  const done = el('button', 'w21-btn w21-ghost w21-block', 'Done');
  done.type = 'button';
  sheet.append(head, body, done);
  overlay.append(sheet);
  const close = (): void => {
    overlay.remove();
    window.removeEventListener('keydown', onKey);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  overlay.addEventListener('click', close);
  sheet.addEventListener('click', (e) => e.stopPropagation());
  sheet.addEventListener('pointerdown', (e) => e.stopPropagation());
  done.addEventListener('click', close);
  window.addEventListener('keydown', onKey);
  document.body.append(overlay);
  done.focus();

  void chestOdds().then((r) => {
    if (!r.ok) return;
    const odds = r.value;
    const pct = (ppm: number): string => `${Number((ppm / 10_000).toFixed(4)).toString()}%`;
    const bp = (b: number): string => `${(b / 100).toFixed(b % 100 === 0 ? 0 : 1)}%`;
    const table = (wide: boolean): HTMLTableElement => el('table', `w21-odds-table${wide ? ' wide' : ''}`);
    const cell = (tag: 'td' | 'th', content: string | Node, num = false): HTMLElement => {
      const c = el(tag, num ? 'num' : undefined);
      c.append(content);
      return c;
    };
    body.replaceChildren();
    body.append(el('h3', 'w21-t-label', 'Each tap'));
    const t1 = table(false);
    const tb1 = el('tbody');
    for (const u of odds.upgradeBp) {
      const tr = el('tr');
      const name = el('td');
      name.append(rarityName(u.from), ' → ', rarityName(u.to));
      tr.append(name, cell('td', bp(u.bp), true));
      tb1.append(tr);
    }
    t1.append(tb1);
    body.append(t1, el('h3', 'w21-t-label', 'Final rarity'));
    const t2 = table(true);
    const th2 = el('thead');
    const hr = el('tr');
    hr.append(cell('th', 'Earned by'));
    for (const r of odds.rarities) hr.append(cell('th', rarityName(r), true));
    th2.append(hr);
    const tb2 = el('tbody');
    for (const s of odds.sources) {
      const tr = el('tr');
      const name = el('td', undefined, KICKER[s.source]);
      if (s.fromRank !== undefined) name.append(el('small', undefined, ` · ${RANKS_FROM[s.fromRank] ?? `rank ${s.fromRank}`}+`));
      tr.append(name);
      for (const r of odds.rarities) tr.append(cell('td', s.finalPpm[r] > 0 ? pct(s.finalPpm[r]) : '—', true));
      tb2.append(tr);
    }
    t2.append(th2, tb2);
    body.append(t2, el('h3', 'w21-t-label', 'Contents'));
    const t3 = table(true);
    const th3 = el('thead');
    const hr3 = el('tr');
    hr3.append(el('th'));
    for (const r of odds.rarities) hr3.append(cell('th', rarityName(r), true));
    th3.append(hr3);
    const tb3 = el('tbody');
    const row = (label: string, value: (r: Rarity) => string): void => {
      const tr = el('tr');
      tr.append(cell('td', label));
      for (const r of odds.rarities) tr.append(cell('td', value(r), true));
      tb3.append(tr);
    };
    row('$CHAIN', (r) => `${odds.contents[r].chain.min}–${odds.contents[r].chain.max}`);
    row('Free entry · rung', (r) => `${bp(odds.contents[r].ticket.chanceBp)} · ${odds.contents[r].ticket.fee}`);
    row(`${odds.boostMultiplier}× XP · games`, (r) => `${bp(odds.contents[r].boost.chanceBp)} · ${odds.contents[r].boost.games}`);
    row('Cosmetic', (r) => bp(odds.contents[r].cosmetic.chanceBp));
    t3.append(th3, tb3);
    body.append(
      t3,
      el(
        'p',
        'w21-odds-foot',
        `Cosmetic: frame ${bp(odds.frameBp)} / title ${bp(10_000 - odds.frameBp)}, never one you own. With every one of a rarity owned it comes from the rarity below; with none left, the chest holds no cosmetic (${odds.cosmetics.length} in all).`,
      ),
    );
  });
}
